import React, {  useState, Fragment, useEffect, useCallback } from 'react';
import { FieldArray, FormikHandlers } from 'formik';
import styled from 'styled-components/macro';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router';

import { DiscountSection } from '../../common/components';
import { Checkbox, Input, Radiobutton } from '../../../components/Forms';
import Card from '../../../components/Card';
import { Campaign, LocationType } from '../../types';
import { State } from '../reducer';
import { distances, colors, border } from '../../../styles/constants';
import Icon from '../../../components/Icons';
import { Button } from '../../../components/Buttons';
import useImportJson from '../../../helpers/json/useImportJson';
import { Muted } from '../../../components/Typography';
import { isDigit } from '../../../helpers/validation/digit';
import { Search } from '../../../components/Filters';

interface DiscountProps {
    locationState: State;
    fetchLocations: (accountId: string, search?: string) => void;
    values: Campaign;
    setFieldValue: (field: string, value: any, shouldValidate?: boolean) => void;
    handleBlur: FormikHandlers['handleBlur'];
    isMccOnly?: boolean;
}

type Mcc = {
    mcc: string;
    range: {
        start: string;
        end: string;
        description: string;
    };
    iso_description: string;
};

const isValidMcc = (value: string) => {
    return value.length === 4 && isDigit(value);
};

const Location = (
    {
        locationState,
        fetchLocations,
        values,
        setFieldValue,
        handleBlur,
        isMccOnly,
    }: DiscountProps
) => {
    const { t } = useTranslation();
    const { accountId } = useParams();
    const [isVisible, setIsVisible] = useState<boolean>(false);
    const [manuallyAddedIds, setManuallyAddedIds] = useState<string>('');
    const [manuallyAddedMccs, setManuallyAddedMccs] = useState<string>('');
    const [manuallyAddedMccRangeStart, setManuallyAddedMccRangeStart] = useState<string>('');
    const [manuallyAddedMccRangeEnd, setManuallyAddedMccRangeEnd] = useState<string>('');
    const mccs = useImportJson<Mcc[]>('./mccs.json', values.locationType === LocationType.mcc); // note, must be a local path from the library

    useEffect(() => {
        if (accountId) {
            fetchLocations(accountId);
        }
    }, [accountId, fetchLocations]);

    const getItemRowText = (id: string) => {
        const location = locationState?.locations?.find((location) => location.location_id === id);
        if (location) {
            return `${location?.location_id} ${location?.name}, ${location?.address?.postal_place}`;
        }
        return id;
    };

    const searchLocations = (search: string) => {
        if (accountId) {
            fetchLocations(accountId, search);
        }
    };

    return (
        <Card
            title={t('marketing.campaign.select_location')}
            subtitle={t('marketing.campaign.select_locations_from_database')}
            shortHr
            marginBottom={distances.tiny}
        >
            <GroupItemsWrapper>
                {!isMccOnly && (
                    <>
                        <Radiobutton
                            name={'values.locationType'}
                            label={t('marketing.campaign.all_locations')}
                            value={values.locationType === LocationType.allLocations}
                            onChange={() => setFieldValue('locationType', LocationType.allLocations)}
                        />
                        <Radiobutton
                            name={`values.locationType`}
                            label={t('marketing.campaign.predefined_list')}
                            value={values.locationType === LocationType.predefinedLocations}
                            onChange={() => setFieldValue('locationType', LocationType.predefinedLocations)}
                        />
                    </>
                )}
            </GroupItemsWrapper>
            {values.locationType === LocationType.mcc && (
                <>
                    <DiscountSection label={t('marketing.campaign.select_mccs')}>
                        <FieldArray
                            name="mccs"
                            render={(arrayHelpers) => (
                                <Fragment>
                                    <InputsContainer>
                                        <FullWidthInputContainer>
                                            <Input
                                                name="manuallyAddedMccs"
                                                label={t('marketing.campaign.add_manually_mccs')}
                                                placeholder={t('marketing.campaign.add_manually_mccs_comma_separated')}
                                                value={manuallyAddedMccs}
                                                onChange={(event) => setManuallyAddedMccs(event.currentTarget.value)}
                                                onBlur={handleBlur}
                                                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                                    // search on enter
                                                    if (
                                                        event.code === 'Enter' ||
                                                        event.key === 'Enter' ||
                                                        event.keyCode === 13
                                                    ) {
                                                        if (manuallyAddedMccs.length > 0) {
                                                            new Set(
                                                                manuallyAddedMccs.split(',').map((x) => x.trim())
                                                            ).forEach(
                                                                (id) =>
                                                                    isValidMcc(id.trim()) &&
                                                                    !values.mccs.includes(id.trim()) &&
                                                                    arrayHelpers.push(id.trim())
                                                            );
                                                            setManuallyAddedMccs('');
                                                        }
                                                        event.preventDefault();
                                                    }
                                                }}
                                            />
                                            <Button
                                                type="button"
                                                className="tiny"
                                                onClick={() => {
                                                    if (manuallyAddedMccs.length > 0) {
                                                        new Set(
                                                            manuallyAddedMccs.split(',').map((x) => x.trim())
                                                        ).forEach(
                                                            (id) =>
                                                                isValidMcc(id.trim()) &&
                                                                !values.mccs.includes(id.trim()) &&
                                                                arrayHelpers.push(id.trim())
                                                        );
                                                        setManuallyAddedMccs('');
                                                    }
                                                }}
                                            >
                                                {t('marketing.campaign.add')}
                                            </Button>
                                        </FullWidthInputContainer>
                                    </InputsContainer>
                                    <Items>
                                        {values.mccs.map((code, storeIndex) => {
                                            const mcc = mccs?.find((entry) => entry.mcc === code);
                                            return (
                                                <ItemRow key={storeIndex}>
                                                    <ItemDescription>
                                                        <ItemButtonInterior>
                                                            <p>
                                                                {code}
                                                                <Hint>
                                                                    {[mcc?.range.description, mcc?.iso_description]
                                                                        .filter((x) => x)
                                                                        .join(' → ')}
                                                                </Hint>
                                                            </p>
                                                        </ItemButtonInterior>
                                                    </ItemDescription>
                                                    <DeleteButton
                                                        type="button"
                                                        onClick={() => {
                                                            arrayHelpers.remove(storeIndex);
                                                        }}
                                                    >
                                                        {'✕'}
                                                    </DeleteButton>
                                                </ItemRow>
                                            );
                                        })}
                                    </Items>
                                </Fragment>
                            )}
                        />
                        <FieldArray
                            name="mccRanges"
                            render={(arrayHelpers) => (
                                <Fragment>
                                    <InputsContainer>
                                        <DualInputContainer>
                                            <Input
                                                name="manuallyAddedMccRangeStart"
                                                label={t('marketing.campaign.add_manually_mcc_range_start')}
                                                placeholder={t(
                                                    'marketing.campaign.add_manually_mcc_range_start_placeholder'
                                                )}
                                                value={manuallyAddedMccRangeStart}
                                                onChange={(event) =>
                                                    setManuallyAddedMccRangeStart(event.currentTarget.value)
                                                }
                                                onBlur={handleBlur}
                                                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                                    // search on enter
                                                    if (
                                                        event.code === 'Enter' ||
                                                        event.key === 'Enter' ||
                                                        event.keyCode === 13
                                                    ) {
                                                        const input = document.querySelector(
                                                            'input[name="manuallyAddedMccRangeEnd"]'
                                                        );
                                                        input && (input as HTMLElement).focus();
                                                        event.preventDefault();
                                                    }
                                                }}
                                            />
                                            <Input
                                                name="manuallyAddedMccRangeEnd"
                                                label={t('marketing.campaign.add_manually_mcc_range_end')}
                                                placeholder={t(
                                                    'marketing.campaign.add_manually_mcc_range_end_placeholder'
                                                )}
                                                value={manuallyAddedMccRangeEnd}
                                                onChange={(event) =>
                                                    setManuallyAddedMccRangeEnd(event.currentTarget.value)
                                                }
                                                onBlur={handleBlur}
                                                onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                                    // search on enter
                                                    if (
                                                        event.code === 'Enter' ||
                                                        event.key === 'Enter' ||
                                                        event.keyCode === 13
                                                    ) {
                                                        if (
                                                            isValidMcc(manuallyAddedMccRangeStart.trim()) &&
                                                            isValidMcc(manuallyAddedMccRangeEnd.trim())
                                                        ) {
                                                            if (
                                                                parseInt(manuallyAddedMccRangeStart.trim()) <
                                                                parseInt(manuallyAddedMccRangeEnd.trim())
                                                            ) {
                                                                arrayHelpers.push([
                                                                    manuallyAddedMccRangeStart.trim(),
                                                                    manuallyAddedMccRangeEnd.trim(),
                                                                ]);
                                                            } else {
                                                                arrayHelpers.push([
                                                                    manuallyAddedMccRangeEnd.trim(),
                                                                    manuallyAddedMccRangeStart.trim(),
                                                                ]);
                                                            }

                                                            setManuallyAddedMccRangeStart('');
                                                            setManuallyAddedMccRangeEnd('');
                                                        }
                                                        event.preventDefault();
                                                    }
                                                }}
                                            />
                                            <Button
                                                type="button"
                                                className="tiny"
                                                onClick={() => {
                                                    if (
                                                        isValidMcc(manuallyAddedMccRangeStart.trim()) &&
                                                        isValidMcc(manuallyAddedMccRangeEnd.trim())
                                                    ) {
                                                        if (
                                                            parseInt(manuallyAddedMccRangeStart.trim()) <
                                                            parseInt(manuallyAddedMccRangeEnd.trim())
                                                        ) {
                                                            arrayHelpers.push([
                                                                manuallyAddedMccRangeStart.trim(),
                                                                manuallyAddedMccRangeEnd.trim(),
                                                            ]);
                                                        } else {
                                                            arrayHelpers.push([
                                                                manuallyAddedMccRangeEnd.trim(),
                                                                manuallyAddedMccRangeStart.trim(),
                                                            ]);
                                                        }
                                                        setManuallyAddedMccRangeStart('');
                                                        setManuallyAddedMccRangeEnd('');
                                                    }
                                                }}
                                            >
                                                {t('marketing.campaign.add')}
                                            </Button>
                                        </DualInputContainer>
                                    </InputsContainer>
                                    <Items>
                                        {values.mccRanges.map(([start, end], storeIndex) => {
                                            const mcc = mccs?.find(
                                                (entry) => entry.range.start === start && entry.range.end === end
                                            );
                                            return (
                                                <ItemRow key={storeIndex}>
                                                    <ItemDescription>
                                                        <ItemButtonInterior>
                                                            <p>
                                                                {start} - {end}
                                                                <Hint>{[mcc?.range.description].filter((x) => x)}</Hint>
                                                            </p>
                                                        </ItemButtonInterior>
                                                    </ItemDescription>
                                                    <DeleteButton
                                                        type="button"
                                                        onClick={() => {
                                                            arrayHelpers.remove(storeIndex);
                                                        }}
                                                    >
                                                        {'✕'}
                                                    </DeleteButton>
                                                </ItemRow>
                                            );
                                        })}
                                    </Items>
                                </Fragment>
                            )}
                        />
                    </DiscountSection>
                </>
            )}
            {values.locationType === LocationType.predefinedLocations && (
                <DiscountSection label={t('marketing.campaign.select_stores')}>
                    <FieldArray
                        name="storeIds"
                        render={(arrayHelpers) => (
                            <Fragment>
                                <InputsContainer>
                                    <DropdownContainer>
                                        <DropdownButton type="button" onClick={() => setIsVisible(!isVisible)}>
                                            {t('marketing.campaign.select_stores')}
                                            <Icon icon="caret_down" />
                                        </DropdownButton>
                                        <ExpandableList visible={isVisible}>
                                            {locationState?.locations?.map((location) => (
                                                <ListItem
                                                    key={location.id}
                                                    onClick={(event) => {
                                                        event.preventDefault();
                                                        location.location_id &&
                                                        values.storeIds.includes(location.location_id)
                                                            ? arrayHelpers.remove(
                                                                values.storeIds.findIndex(
                                                                    (id) => id === location.location_id
                                                                )
                                                            )
                                                            : arrayHelpers.push(location.location_id);
                                                    }}
                                                >
                                                    <Checkbox
                                                        name="storeIds"
                                                        label={`${location.location_id} ${location.name}, ${location?.address?.postal_place}`}
                                                        value={location.location_id}
                                                        checked={
                                                            location.location_id
                                                                ? values.storeIds.includes(location.location_id)
                                                                : false
                                                        }
                                                        onChange={() => {}}
                                                    />
                                                </ListItem>
                                            ))}
                                            <SearchWrapper>
                                                <Search
                                                    name="searchStoreIds"
                                                    placeholder={t('marketing.campaign.search_store_id')}
                                                    defaultValue=""
                                                    onChange={(value) => searchLocations(value)}
                                                    onBlur={(value) => searchLocations(value)}
                                                    onKeyPress={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                                        // search on enter
                                                        if (
                                                            event.code === 'Enter' ||
                                                            event.key === 'Enter' ||
                                                            event.keyCode === 13
                                                        ) {
                                                            searchLocations(event.currentTarget.value);
                                                            event.preventDefault();
                                                        }
                                                    }}
                                                    debounceTimeoutMs={500}
                                                    className="full-width"
                                                    hideIcon
                                                />
                                            </SearchWrapper>
                                            <ButtonWrapper>
                                                <Button
                                                    type="button"
                                                    className="tiny"
                                                    onClick={() => setIsVisible(false)}
                                                >
                                                    {t('marketing.campaign.ok')}
                                                </Button>
                                            </ButtonWrapper>
                                        </ExpandableList>
                                    </DropdownContainer>
                                    <ManualInputContainer>
                                        <Input
                                            name="manuallyAddedIds"
                                            label={t('marketing.campaign.add_manually_store_ids')}
                                            placeholder={t('marketing.campaign.store_ids_comma_separated')}
                                            value={manuallyAddedIds}
                                            onChange={(event) => setManuallyAddedIds(event.currentTarget.value)}
                                            onBlur={handleBlur}
                                            onKeyDown={(event: React.KeyboardEvent<HTMLInputElement>) => {
                                                // search on enter
                                                if (
                                                    event.code === 'Enter' ||
                                                    event.key === 'Enter' ||
                                                    event.keyCode === 13
                                                ) {
                                                    if (manuallyAddedIds.length > 0) {
                                                        new Set(
                                                            manuallyAddedIds.split(',').map((x) => x.trim())
                                                        ).forEach(
                                                            (id) =>
                                                                id.trim() &&
                                                                !values.storeIds.includes(id.trim()) &&
                                                                arrayHelpers.push(id.trim())
                                                        );
                                                        setManuallyAddedIds('');
                                                    }
                                                }
                                            }}
                                        />
                                        <Button
                                            type="button"
                                            className="tiny"
                                            onClick={() => {
                                                if (manuallyAddedIds.length > 0) {
                                                    if (manuallyAddedIds.length > 0) {
                                                        new Set(
                                                            manuallyAddedIds.split(',').map((x) => x.trim())
                                                        ).forEach(
                                                            (id) =>
                                                                id.trim() &&
                                                                !values.storeIds.includes(id.trim()) &&
                                                                arrayHelpers.push(id.trim())
                                                        );
                                                        setManuallyAddedIds('');
                                                    }
                                                }
                                            }}
                                        >
                                            {t('marketing.campaign.add')}
                                        </Button>
                                    </ManualInputContainer>
                                </InputsContainer>
                                {values.storeIds.map((id, storeIndex) => (
                                    <ItemRow key={storeIndex}>
                                        <ItemButton type="button">
                                            <ItemButtonInterior>
                                                {} <p>{getItemRowText(id)}</p>
                                            </ItemButtonInterior>
                                        </ItemButton>
                                        <DeleteButton
                                            type="button"
                                            onClick={() => {
                                                arrayHelpers.remove(storeIndex);
                                            }}
                                        >
                                            {'✕'}
                                        </DeleteButton>
                                    </ItemRow>
                                ))}
                            </Fragment>
                        )}
                    />
                </DiscountSection>
            )}
        </Card>
    );
};

const GroupItemsWrapper = styled.div`
    display: flex;
    width: 100%;
    justify-content: space-between;
    margin: ${distances.normal} 0;

    > *:first-child {
        margin-right: ${distances.small};
    }

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const Items = styled.div`
    margin-bottom: ${distances.small};
`;
const ItemRow = styled.div`
    display: flex;
    justify-content: space-between;
`;

const ItemButton = styled.button`
    width: 100%;
    background: none;
    border: none;
    border-bottom: ${border.normal} solid ${colors.borderDark};
    cursor: pointer;

    &:focus {
        outline: none;
        border-bottom: ${border.normal} solid ${colors.primary};
    }
`;
const ItemDescription = styled.div`
    width: 100%;
    background: none;
    border: none;
    border-bottom: ${border.normal} solid ${colors.borderDark};
    cursor: pointer;
`;

const ItemButtonInterior = styled.div`
    display: flex;
    justify-content: space-between;
`;

const DeleteButton = styled.button`
    width: ${distances.large};
    color: ${colors.invalid};
    text-align: end;
    background: none;
    border: none;
    cursor: pointer;

    &:focus {
        outline: none;
    }
`;

const InputsContainer = styled.div`
    display: flex;
    justify-content: space-between;

    @media (max-width: 768px) {
        display: block;
    }
`;

const DropdownContainer = styled.div`
    position: relative;
    width: 45%;

    @media (max-width: 768px) {
        width: 100%;
    }
`;

const DropdownButton = styled.button`
    width: 100%;
    padding: 20px ${distances.small};
    margin: 0;
    border-radius: 2px;
    display: flex;
    justify-content: space-between;
    outline: none;
    align-items: center;
    background-color: ${colors.background};
    border: none;
    border-bottom: 1px solid ${colors.borderDark};
    cursor: pointer;
`;

const ListItem = styled.div`
    padding: ${distances.small};
    cursor: pointer;
`;

const ExpandableList = styled.div<{ visible: boolean }>`
    position: absolute;
    width: 100%;
    display: ${(props) => (props.visible ? 'block' : 'none')};
    z-index: 90;
    outline: none;
    background-color: ${colors.background};
    box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
`;

const ManualInputContainer = styled.div`
    display: flex;
    width: 45%;
    justify-content: space-between;
    align-items: center;

    > *:first-child {
        width: 80%;
    }

    @media (max-width: 768px) {
        width: 100%;
        margin-top: ${distances.normal};
    }
`;

const FullWidthInputContainer = styled(ManualInputContainer)`
    width: 100%;
`;

const DualInputContainer = styled(ManualInputContainer)`
    width: 100%;
    > *:first-child {
        width: 40%;
    }
`;
const Hint = styled(Muted)`
    margin-left: ${distances.small};
    color: ${colors.textSecondary};
`;

const ButtonWrapper = styled.div`
    display: flex;
    padding: 0 ${distances.small} ${distances.small};
    justify-content: flex-end;
`;

const SearchWrapper = styled.div`
    margin: 16px;
`;

export default Location;
